import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  at,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  hudRegion,
  notification,
  productScreen,
  panel,
  updatePanel,
  removePanel,
  spawnVehicle,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';
import { assetUrl } from '../../../lib/assets';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO — CUSTOMER-CENTRED & SUSTAINABLE STREET WORKS (MAINS REHAB)
//
// Use case: A contractor working 3 utility companies' permits in one area.
// Water, Gas, Electric — all managed on a single platform.
//
// Events simulated: Works Start → Works Overdue → 2-hour Defect →
// Overrun on a Major Road → Reinstatement Defect → Permit Extension
//
// UNIQUE VISUAL DNA:
// 1. THREE simultaneous excavation sites (one per utility) — colour-coded
//    model-status: Water=blue, Gas=amber, Electric=purple
// 2. Multi-site-rag with UTILITY BRANDING not geographic districts
// 3. Vehicles dispatched between companies (Engineering → Reinstatement)
// 4. flow-pulse as PERMIT RISK flow between sites and a management hub
// 5. Excavation holes that OPEN, go into DEFECT (red glow), then CLEAR
// 6. comms-broadcast for customer notifications on permit extension
// 7. Live risk panel shifting as events cascade
//
// No pipe-lay (this is about the permit lifecycle, not the pipe).
// ──────────────────────────────────────────────────────────────────────────

// ─── Sites (spread across a busy area — major road zone) ────────────────
const MRP_HUB = infra('control-centre', gp(-2, -4));

// Three utility permit sites
const WATER_SITE = infra('pump-alpha', gp(18, 19));     // Water utility
const GAS_SITE = infra('gas-reg', gp(7, 25));           // Gas utility
const ELECTRIC_SITE = infra('sub-south', gp(11, 22));   // Electric utility

// Excavation positions at each site
const WATER_HOLES: Array<{ coords: [number, number]; id: string }> = [
  { coords: gp(17, 19), id: 'water-hole-1' },
  { coords: gp(19, 19), id: 'water-hole-2' },
];
const GAS_HOLES: Array<{ coords: [number, number]; id: string }> = [
  { coords: gp(6, 25), id: 'gas-hole-1' },
  { coords: gp(8, 25), id: 'gas-hole-2' },
];
const ELECTRIC_HOLES: Array<{ coords: [number, number]; id: string }> = [
  { coords: gp(10, 22), id: 'elec-hole-1' },
  { coords: gp(12, 22), id: 'elec-hole-2' },
];

// Nearby properties for comms
const NEARBY_PROPERTIES: [number, number][] = [
  gp(16, 18), gp(18, 18), gp(20, 18),
  gp(16, 20), gp(18, 20), gp(20, 20),
  gp(6, 24), gp(8, 24), gp(6, 26), gp(8, 26),
  gp(10, 21), gp(12, 21), gp(10, 23), gp(12, 23),
];

// Risk flow paths (sites → management hub)
const RISK_WATER: [number, number][] = [gp(18, 19), gp(12, 12), gp(4, 4), gp(-2, -4)];
const RISK_GAS: [number, number][] = [gp(7, 25), gp(4, 16), gp(0, 6), gp(-2, -4)];

function highlightAt(coords: [number, number], radius = 100): ScenarioStep {
  return { type: 'highlight-buildings', duration: 0, params: { coordinates: coords, radius } };
}

export const streetWorksPermitsScenario = defineScenario(
  {
    id: 'street-works-permits',
    title: 'Multi-Utility Street Works — Permit Lifecycle Management',
    description: '3 utilities, permit events, live risk, customer comms, dispatch to reinstatement, resolution',
    hudLabel: 'MRP LIVE',
    telemetry: 'street-works-permits',
    activeInfraIds: ['control-centre', 'pump-alpha', 'gas-reg', 'sub-south'],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // ACT 1: EXEC VIEW — Control Room MRP Live (0–25s)
    // Wide view showing all 3 utility sites. Looping by utility then by
    // contractor. Multi-site-rag shows all permits green initially.
    // ═══════════════════════════════════════════════════════════════════════
    act('MRP Live', [
      dimStart,

      // Full portfolio view — 3 utilities visible as constellation
      beat(PACING.ESTABLISH, [
        establishingWide(MRP_HUB, { bearing: -10, pitch: 42, zoom: 14 }),
        { type: 'multi-site-rag', duration: 0, params: {
          id: 'permits',
          sites: [
            { coordinates: gp(18, 19), status: 'green' },
            { coordinates: gp(7, 25), status: 'green' },
            { coordinates: gp(11, 22), status: 'green' },
          ],
          linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
        }},
        { type: 'model-status', duration: 0, params: { coordinates: gp(-2, -4), infraId: 'control-centre', color: '#0ea5e9', emissive: 0.4 } },
        hudEvent('programme', 'MRP LIVE — CONTROL ROOM', '3 utility permits active. Contractor: Morrison Utility Services. All permits in window.', 'info'),
        hudRegion([
          { name: 'Water Co — Permit W-2847', status: 'active', properties: 2 },
          { name: 'Gas Co — Permit G-1193', status: 'active', properties: 2 },
          { name: 'Electric Co — Permit E-0562', status: 'active', properties: 2 },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'permits-active', kind: 'counter', label: 'Active Permits', value: 3, unit: '', severity: 'success' } },
        { type: 'hud-metric', duration: 0, params: { id: 'risk', kind: 'counter', label: 'Live Risk', value: 12, unit: '%', severity: 'success' } },
      ]),

      // Colour-code each utility site
      beat(PACING.PUSH_IN, [
        { type: 'camera', duration: PACING.PUSH_IN, params: { center: gp(12, 22), zoom: 14.8, pitch: 45, bearing: 15 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(18, 19), infraId: 'pump-alpha', color: '#0ea5e9', emissive: 0.5 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(7, 25), infraId: 'gas-reg', color: '#f59e0b', emissive: 0.5 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(11, 22), infraId: 'sub-south', color: '#a855f7', emissive: 0.5 } },
        hudTeam([
          { name: 'Water Co (Blue)', status: 'active', destination: 'Minor Works' },
          { name: 'Gas Co (Amber)', status: 'active', destination: 'Emergency' },
          { name: 'Electric Co (Purple)', status: 'active', destination: 'Major Works' },
        ]),
        notification('3 UTILITIES — 1 PLATFORM', 'All permits managed by single contractor on unified MRP system.', 'info', PACING.BEAT),
      ]),

      productScreen('Control Room — MRP Live', {
        image: assetUrl('/screens/sw-permit-costs-dashboard.png'),
        device: 'desktop',
        subtitle: 'Permit costs overview — application costs, fines, penalties and compliance',
        role: 'Programme Manager',
        url: 'app.skewb.io/mrp/control-room',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 2: PERMIT CARDS + WORKS START — Excavations open (25–55s)
    // Visit each site. Show permit cards for all 3. Open excavation holes.
    // ═══════════════════════════════════════════════════════════════════════
    act('Permit Cards & Works Start', [
      // Water site — permit card
      beat(6000, [
        dronePushIn(WATER_SITE, { bearing: -20, zoom: 16, duration: 6000 }),
        panel('permit-water', 'Permit W-2847 — Water Co', 'data', WATER_SITE, [
          { label: 'Type', value: 'Minor Works' },
          { label: 'Road', value: 'Elm Avenue (C-road)' },
          { label: 'Duration', value: '3 days', status: 'good' },
          { label: 'Status', value: 'IN WINDOW', status: 'good' },
        ]),
        hudEvent('permit', 'PERMIT W-2847', 'Water Co — Minor works, Elm Avenue. S74 active. 3-day window.', 'info'),
      ]),
      removePanel('permit-water'),

      // Gas site — permit card
      beat(5000, [
        dronePushIn(GAS_SITE, { bearing: 10, zoom: 16, duration: 5000 }),
        panel('permit-gas', 'Permit G-1193 — Gas Co', 'data', GAS_SITE, [
          { label: 'Type', value: 'Emergency' },
          { label: 'Road', value: 'Park Road (B-road)' },
          { label: 'Duration', value: '2 hours', status: 'warning' },
          { label: 'Status', value: 'IN WINDOW', status: 'good' },
        ]),
        hudEvent('permit', 'PERMIT G-1193', 'Gas Co — Emergency repair, Park Road. 2-hour emergency window.', 'warning'),
      ]),
      removePanel('permit-gas'),

      // Electric site — permit card
      beat(5000, [
        dronePushIn(ELECTRIC_SITE, { bearing: 30, zoom: 16, duration: 5000 }),
        panel('permit-elec', 'Permit E-0562 — Electric Co', 'data', ELECTRIC_SITE, [
          { label: 'Type', value: 'Major Works' },
          { label: 'Road', value: 'High Street (A-road)' },
          { label: 'Duration', value: '10 days', status: 'good' },
          { label: 'Status', value: 'IN WINDOW', status: 'good' },
        ]),
        hudEvent('permit', 'PERMIT E-0562', 'Electric Co — Major works, High Street. 10-day window. Lane closure active.', 'info'),
      ]),
      removePanel('permit-elec'),

      // WORKS START — open all excavation holes
      ...WATER_HOLES.map((h) => ({
        type: 'excavation' as const,
        duration: 4000,
        params: { coordinates: h.coords, radius: 12, id: h.id },
      })),
      ...GAS_HOLES.map((h) => ({
        type: 'excavation' as const,
        duration: 4000,
        params: { coordinates: h.coords, radius: 10, id: h.id },
      })),
      ...ELECTRIC_HOLES.map((h) => ({
        type: 'excavation' as const,
        duration: 4000,
        params: { coordinates: h.coords, radius: 14, id: h.id },
      })),

      beat(PACING.BEAT, [
        craneReveal(MRP_HUB, { zoom: 14.5, bearing: 0 }),
        hudEvent('works', 'ALL WORKS STARTED', '6 excavations open across 3 utility sites. All permits in window.', 'info'),
        notification('WORKS COMMENCED', '3 utilities — 6 excavations — all within permit window.', 'success', PACING.BEAT),
        highlightAt(gp(18, 19), 150),
        highlightAt(gp(7, 25), 150),
        highlightAt(gp(11, 22), 150),
      ]),

      productScreen('Permit Cards — All Utilities', {
        image: assetUrl('/screens/sw-executive-summary.png'),
        device: 'desktop',
        subtitle: 'Streetworks executive summary — total records, active works, inspections',
        role: 'Permit Manager',
        url: 'app.skewb.io/mrp/permits',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 3: LIVE RISK — Events cascade (55–100s)
    // Works Overdue → 2-hour Defect → Overrun on Major Road.
    // Risk flow-pulse lines appear. Sites go red. Permit extension needed.
    // Customer comms triggered (SMS, WhatsApp, Permit Board).
    // ═══════════════════════════════════════════════════════════════════════
    act('Live Risk & Events', [
      // Gas permit — 2-hour defect (emergency overrun)
      { type: 'model-status', duration: 0, params: { coordinates: gp(7, 25), infraId: 'gas-reg', color: '#ef4444', emissive: 0.8 } } as ScenarioStep,

      beat(6000, [
        dronePushIn(GAS_SITE, { bearing: -10, zoom: 16.5, duration: 6000 }),
        hudEvent('alert', '2-HOUR DEFECT — GAS', 'Permit G-1193 exceeded 2-hour emergency window. Defect period started.', 'critical'),
        { type: 'hud-metric', duration: 0, params: { id: 'defect', kind: 'countdown', label: 'Defect Timer', value: 120, unit: 'min', severity: 'critical' } },
        { type: 'hud-metric', duration: 0, params: { id: 'risk', kind: 'counter', label: 'Live Risk', value: 58, unit: '%', severity: 'critical' } },
      ]),

      // Risk flow line — gas site to hub
      { type: 'flow-pulse', duration: 0, params: {
        id: 'risk-gas', path: RISK_GAS, color: '#ef4444', speed: 1.8,
      }} as ScenarioStep,

      // Update constellation — gas goes red
      { type: 'clear-effect', duration: 0, params: { id: 'permits' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'permits',
        sites: [
          { coordinates: gp(18, 19), status: 'green' },
          { coordinates: gp(7, 25), status: 'red' },
          { coordinates: gp(11, 22), status: 'green' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      // Electric permit — overrun on major road
      beat(6000, [
        dronePushIn(ELECTRIC_SITE, { bearing: 40, zoom: 16.5, duration: 6000 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(11, 22), infraId: 'sub-south', color: '#ef4444', emissive: 0.8 } },
        hudEvent('alert', 'OVERRUN — MAJOR ROAD', 'Permit E-0562 works overrunning on A-road. Lane closure extended. S74 risk.', 'critical'),
        { type: 'color-grade', duration: 0, params: { tint: '#ef4444', intensity: 0.05, vignette: 0.18 } },
        notification('MAJOR ROAD OVERRUN', 'High Street works extended. Permit extension required or £5K/day penalty.', 'critical', 5000),
      ]),

      // Water permit — reinstatement defect found
      { type: 'model-status', duration: 0, params: { coordinates: gp(18, 19), infraId: 'pump-alpha', color: '#f59e0b', emissive: 0.6 } } as ScenarioStep,

      // Risk flow line — water site
      { type: 'flow-pulse', duration: 0, params: {
        id: 'risk-water', path: RISK_WATER, color: '#f59e0b', speed: 1.5,
      }} as ScenarioStep,

      // Update constellation — all at risk
      { type: 'clear-effect', duration: 0, params: { id: 'permits' } } as ScenarioStep,
      { type: 'multi-site-rag', duration: 0, params: {
        id: 'permits',
        sites: [
          { coordinates: gp(18, 19), status: 'amber' },
          { coordinates: gp(7, 25), status: 'red' },
          { coordinates: gp(11, 22), status: 'red' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      beat(PACING.BEAT, [
        craneReveal(MRP_HUB, { zoom: 14.5, bearing: -10 }),
        hudEvent('risk', 'LIVE RISK ESCALATED', '2 permits in exception. 1 at risk. Permit extension and customer comms required.', 'critical'),
        panel('risk-panel', 'Live Risk — All Utilities', 'data', MRP_HUB, [
          { label: 'Water Co', value: 'Reinst. Defect', status: 'warning' },
          { label: 'Gas Co', value: '2-Hour Breach', status: 'critical' },
          { label: 'Electric Co', value: 'Major Overrun', status: 'critical' },
          { label: 'Penalty Exposure', value: '£5K/day', status: 'critical' },
        ]),
      ]),

      removePanel('risk-panel'),

      productScreen('Live Risk — All Utilities', {
        image: assetUrl('/screens/sw-live-risk.png'),
        device: 'desktop',
        subtitle: 'Street Works Risks — high risk permits, overdue, defects, expiring',
        role: 'Risk Manager',
        url: 'app.skewb.io/mrp/live-risk',
      }),

      // CUSTOMER COMMS — permit extension notification
      beat(8000, [
        orbit180(ELECTRIC_SITE, { startBearing: 40, arc: 100, zoom: 15.5, pitch: 42, duration: 8000 }),
        // SMS (green)
        { type: 'comms-broadcast', duration: 8000, params: {
          infraId: 'sub-south',
          coordinates: gp(11, 22),
          color: '#10b981',
          radius: 160,
          targets: NEARBY_PROPERTIES.slice(10),
          id: 'comms-sms',
        }},
        // WhatsApp (green-darker)
        { type: 'comms-broadcast', duration: 8000, params: {
          coordinates: gp(18, 19),
          color: '#22c55e',
          radius: 140,
          targets: NEARBY_PROPERTIES.slice(0, 6),
          id: 'comms-whatsapp',
        }},
        highlightAt(gp(18, 19), 150),
        highlightAt(gp(11, 22), 150),
        highlightAt(gp(7, 25), 120),
        hudEvent('comms', 'CUSTOMER COMMS — PERMIT EXTENSION', 'SMS, WhatsApp, and Permit Board notifications sent. Explaining extension reason.', 'info'),
        notification('PERMIT EXTENSION NOTIFIED', 'Customers informed: works extended 2 days due to reinstatement defect.', 'info', 5000),
        hudTeam([
          { name: 'SMS Sent', status: 'complete', destination: '14 properties' },
          { name: 'WhatsApp Sent', status: 'complete', destination: '14 properties' },
          { name: 'Permit Board Updated', status: 'complete', destination: 'Public notice' },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'comms-sms' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'comms-whatsapp' } } as ScenarioStep,
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 4: DISPATCH & REINSTATEMENT — Vehicles, material class (100–140s)
    // Engineering → Reinstatement company handoff. Vehicles dispatched.
    // Material classification outcomes. Job via Core to Reinstatement + Grab.
    // ═══════════════════════════════════════════════════════════════════════
    act('Dispatch & Reinstatement', [
      { type: 'color-grade', duration: 0, params: { tint: '#f59e0b', intensity: 0.04, vignette: 0.12 } } as ScenarioStep,

      beat(PACING.PUSH_IN, [
        dronePushIn(at(9, 23), { bearing: -20, zoom: 15 }),
        hudEvent('dispatch', 'DISPATCH — REINSTATEMENT', 'Works handed from Engineering to Reinstatement Company. Material classification complete.', 'info'),
        panel('dispatch', 'Dispatch — Core', 'data', ELECTRIC_SITE, [
          { label: 'From', value: 'Morrison Engineering' },
          { label: 'To', value: 'Reinstatement Co' },
          { label: 'Material', value: 'Type 2 (Class B)', status: 'warning' },
          { label: 'Excavation Size', value: '2.4m x 1.8m' },
        ]),
      ]),

      // Vehicle: Engineering van leaves, Reinstatement van arrives
      spawnVehicle('eng-van', 'kenney-van', ELECTRIC_SITE, at(0, 30), 'Engineering', 8000),

      beat(6000, [
        orbit180(ELECTRIC_SITE, { startBearing: -20, arc: 80, zoom: 15.5, pitch: 50, duration: 6000 }),
        hudEvent('handoff', 'ENGINEERING DEPARTED', 'Engineering team released. Reinstatement team inbound with correct materials.', 'info'),
      ]),

      spawnVehicle('reinst-van', 'kenney-truck', at(0, 30), ELECTRIC_SITE, 'Reinstatement', 8000),

      beat(6000, [
        { type: 'camera', duration: 6000, params: { center: gp(11, 22), infraId: 'sub-south', zoom: 16, pitch: 55, bearing: 20 } },
        hudEvent('arrive', 'REINSTATEMENT ARRIVED', 'Reinstatement team on site. Job dispatched via Core. Grab company en route.', 'success'),
        updatePanel('dispatch', [
          { label: 'Reinstatement', value: 'On Site', status: 'good' },
          { label: 'Grab Company', value: 'En Route', status: 'warning' },
          { label: 'Material Match', value: 'Confirmed', status: 'good' },
          { label: 'Est. Completion', value: '2 hours' },
        ]),
      ]),

      // Grab truck dispatched
      spawnVehicle('grab-truck', 'kenney-truck-flat', at(0, 30), GAS_SITE, 'Grab Removal', 7000),

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(9, 24), zoom: 15, pitch: 48, bearing: 0 } },
        hudTeam([
          { name: 'Reinstatement Co', status: 'on-site', destination: 'Elec + Water sites' },
          { name: 'Grab Removal Co', status: 'on-site', destination: 'Gas site spoil' },
          { name: 'Engineering Co', status: 'complete', destination: 'Departed' },
        ]),
        notification('DISPATCH COMPLETE', 'Reinstatement and Grab teams deployed. All material classified.', 'success', PACING.BEAT),
      ]),

      removePanel('dispatch'),

      productScreen('Core — Job Dispatch', {
        image: assetUrl('/screens/sw-contractor-scorecards.png'),
        device: 'desktop',
        subtitle: 'Contractor scorecards — performance metrics, inspections, compliance',
        role: 'Dispatch Manager',
        url: 'app.skewb.io/core/dispatch',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 5: RESOLUTION — Reinstated, permits closed (140–175s)
    // Holes close. Permit status vs work complete. Management dashboard.
    // All sites green. Risk flows cleared.
    // ═══════════════════════════════════════════════════════════════════════
    act('Reinstated', [
      // Clear all holes
      ...WATER_HOLES.map((h) => ({ type: 'clear-effect' as const, duration: 0, params: { id: h.id } })),
      ...GAS_HOLES.map((h) => ({ type: 'clear-effect' as const, duration: 0, params: { id: h.id } })),
      ...ELECTRIC_HOLES.map((h) => ({ type: 'clear-effect' as const, duration: 0, params: { id: h.id } })),
      { type: 'clear-effect', duration: 0, params: { id: 'risk-gas' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'risk-water' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'defect' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'risk' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'permits-active' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'permits' } } as ScenarioStep,

      // All sites go green
      { type: 'model-status', duration: 0, params: { coordinates: gp(18, 19), infraId: 'pump-alpha', color: '#10b981', emissive: 0.5 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(7, 25), infraId: 'gas-reg', color: '#10b981', emissive: 0.5 } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(11, 22), infraId: 'sub-south', color: '#10b981', emissive: 0.5 } } as ScenarioStep,

      { type: 'color-grade', duration: 0, params: { tint: '#10b981', intensity: 0.05, vignette: 0.1 } } as ScenarioStep,

      { type: 'multi-site-rag', duration: 0, params: {
        id: 'permits-resolved',
        sites: [
          { coordinates: gp(18, 19), status: 'green' },
          { coordinates: gp(7, 25), status: 'green' },
          { coordinates: gp(11, 22), status: 'green' },
        ],
        linkTo: { coordinates: gp(-2, -4), infraId: 'control-centre' },
      }} as ScenarioStep,

      dimEnd,

      beat(PACING.PUSH_IN, [
        craneReveal(MRP_HUB, { zoom: 14.5, bearing: 10 }),
        hudEvent('complete', 'ALL PERMITS CLOSED', '3 utilities — all works reinstated. Zero S74 penalty. Permits closed within extension.', 'success'),
        notification('REINSTATEMENT COMPLETE', 'All 6 holes reinstated. Surfaces to HAUC spec. Roads reopened.', 'success', PACING.BEAT),
        { type: 'hud-metric', duration: 0, params: { id: 'risk', kind: 'counter', label: 'Live Risk', value: 0, unit: '%', severity: 'success' } },
      ]),

      // Management dashboard — permit status vs work
      beat(8000, [
        orbit180(MRP_HUB, { startBearing: 10, arc: 120, zoom: 14.5, pitch: 45, duration: 8000 }),
        panel('mgmt', 'Management — Permit Compliance', 'data', MRP_HUB, [
          { label: 'Permits Closed', value: '3 / 3', status: 'good' },
          { label: 'S74 Penalty', value: '£0', status: 'good' },
          { label: 'Extension Used', value: '1 (Electric)', status: 'warning' },
          { label: 'Customer CSAT', value: '8.7 / 10', status: 'good' },
        ]),
        hudRegion([
          { name: 'Water Co', status: 'closed', properties: 2 },
          { name: 'Gas Co', status: 'closed', properties: 2 },
          { name: 'Electric Co', status: 'closed', properties: 2 },
        ]),
      ]),

      removePanel('mgmt'),
      { type: 'clear-effect', duration: 0, params: { id: 'permits-resolved' } } as ScenarioStep,

      productScreen('Reinstatement Manager — Live', {
        image: assetUrl('/screens/sw-duration-overrun.png'),
        device: 'desktop',
        subtitle: 'Duration & Overrun analysis — daily overruns, average duration trend',
        role: 'Reinstatement Manager',
        url: 'app.skewb.io/mrp/reinstatement',
      }),

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.04, vignette: 0.1 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -20 }),
        notification('MRP DELIVERED', '3 utilities — 6 excavations — zero penalty — permit lifecycle complete.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);
